import { tokenHolder } from './token-holder'
import { logger } from '@/utils/logger'

const LEGACY_DEFAULT_USER_ID = '33cea32b-56dc-4c60-aef9-2928f57b3f61'
const INVALID_USER_IDS = new Set(['anonymous', 'default'])
const USER_ID_QUERY_KEYS = ['userId', 'user_id']
const PROJECT_ID_QUERY_KEYS = ['projectId', 'project_id']
const TOKEN_USER_ID_KEYS = ['userID', 'user_id', 'userId', 'userid', 'id']
const PROJECT_ID_STORAGE_KEY = 'projectId'

// 与后端 apps/agent-service/src/platform/core/rls_context.py:validate_project_id 对齐：
// 合法 project_id ⇔ 纯数字字符串 OR 标准 8-4-4-4-12 UUID
const PROJECT_ID_DIGIT_RE = /^\d+$/
const PROJECT_ID_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const PROJECT_ID_MAX_LEN = 128

function normalizeIdentityValue(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value !== 'string' && typeof value !== 'number') {
    return null
  }

  const normalized = String(value).trim()
  if (!normalized || INVALID_USER_IDS.has(normalized)) {
    return null
  }

  return normalized
}

/**
 * 校验 project_id 格式（与后端 RLS 规则严格对齐）。
 * 拒绝：slug、半成品 UUID、空串、非字符串、超长。
 */
export function isValidProjectId(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false
  }
  const v = value.trim()
  if (!v || v.length > PROJECT_ID_MAX_LEN) {
    return false
  }
  return PROJECT_ID_DIGIT_RE.test(v) || PROJECT_ID_UUID_RE.test(v)
}

function normalizeProjectIdValue(value: unknown): string | null {
  const normalized = normalizeIdentityValue(value)
  if (normalized === null || !isValidProjectId(normalized)) {
    return null
  }
  return normalized
}

function readQueryValue(keys: string[]): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const urlParams = new URLSearchParams(window.location.search)
  for (const key of keys) {
    const value = normalizeIdentityValue(urlParams.get(key))
    if (value) {
      return value
    }
  }

  return null
}

function persistLocalValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // 忽略本地存储不可用场景，避免影响主流程
  }
}

function persistSessionValue(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // 忽略当前 tab 存储不可用场景，避免影响主流程
  }
}

function readLocalValue(key: string): string | null {
  try {
    return normalizeIdentityValue(localStorage.getItem(key))
  } catch {
    return null
  }
}

function readSessionValue(key: string): string | null {
  try {
    return normalizeIdentityValue(sessionStorage.getItem(key))
  } catch {
    return null
  }
}

/**
 * 规范化用户展示名。
 *
 * 统一处理统一登录平台/网关偶发返回的 URL 编码显示名，例如：
 * `%25E5%25A4%25A7...` -> `%E5%A4%A7...` -> `大数据管理员`。
 */
export function normalizeUserDisplayName(value: unknown): string {
  const normalized = normalizeIdentityValue(value)
  if (!normalized) {
    return ''
  }

  let decoded = normalized
  for (let i = 0; i < 3; i += 1) {
    if (!/%[0-9a-f]{2}/i.test(decoded)) {
      break
    }
    try {
      const next = decodeURIComponent(decoded)
      if (next === decoded) {
        break
      }
      decoded = next.trim()
    } catch {
      break
    }
  }

  // 仍然像 URL 编码串时不要污染 UI，交给调用方使用邮箱/默认名兜底。
  if (/%[0-9a-f]{2}/i.test(decoded) && decoded.length > 24) {
    return ''
  }

  return decoded.trim()
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const normalizedToken = token.trim()
  if (!normalizedToken) {
    return null
  }

  const segments = normalizedToken.split('.')
  if (segments.length < 2) {
    return null
  }

  try {
    const base64 = segments[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(segments[1].length / 4) * 4, '=')

    return JSON.parse(atob(base64)) as Record<string, unknown>
  } catch {
    return null
  }
}

function readUserIdFromToken(): string | null {
  const payload = decodeJwtPayload(tokenHolder.getAccessToken())
  if (!payload) {
    return null
  }

  for (const key of TOKEN_USER_ID_KEYS) {
    const userId = normalizeIdentityValue(payload[key])
    if (userId) {
      return userId
    }
  }

  return null
}

function readQueryProjectId(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  const urlParams = new URLSearchParams(window.location.search)
  for (const key of PROJECT_ID_QUERY_KEYS) {
    const candidate = normalizeProjectIdValue(urlParams.get(key))
    if (candidate) {
      return candidate
    }
  }
  return null
}

function readStoredProjectId(): string | null {
  const sessionProjectId = normalizeProjectIdValue(readSessionValue(PROJECT_ID_STORAGE_KEY))
  if (sessionProjectId) {
    return sessionProjectId
  }

  try {
    const legacyLocalProjectId = normalizeProjectIdValue(localStorage.getItem(PROJECT_ID_STORAGE_KEY))
    if (legacyLocalProjectId) {
      persistSessionValue(PROJECT_ID_STORAGE_KEY, legacyLocalProjectId)
      return legacyLocalProjectId
    }
    return null
  } catch {
    return null
  }
}

function removeLocalProjectId(): void {
  try {
    localStorage.removeItem(PROJECT_ID_STORAGE_KEY)
  } catch {
    // 忽略：localStorage 不可用时无需清理
  }
}

function removeSessionProjectId(): void {
  try {
    sessionStorage.removeItem(PROJECT_ID_STORAGE_KEY)
  } catch {
    // 忽略：sessionStorage 不可用时无需清理
  }
}

function persistProjectId(projectId: string): void {
  persistSessionValue(PROJECT_ID_STORAGE_KEY, projectId)
}

export function setProjectId(projectId: string | null | undefined): string {
  const explicitProjectId = normalizeProjectIdValue(projectId)
  if (explicitProjectId) {
    persistProjectId(explicitProjectId)
    return explicitProjectId
  }
  return getProjectId()
}

export function getProjectId(projectId?: string | null): string {
  const explicitProjectId = normalizeProjectIdValue(projectId)
  if (explicitProjectId) {
    persistProjectId(explicitProjectId)
    return explicitProjectId
  }

  const urlProjectId = readQueryProjectId()
  if (urlProjectId) {
    persistProjectId(urlProjectId)
    return urlProjectId
  }

  const storedProjectId = readStoredProjectId()
  if (storedProjectId) {
    return storedProjectId
  }

  const envDefault = normalizeProjectIdValue(import.meta.env.VITE_DEFAULT_PROJECT_ID)
  const defaultProjectId = envDefault ?? '114'
  persistProjectId(defaultProjectId)
  return defaultProjectId
}

export function resolveProjectId(projectId?: string | null): string {
  return getProjectId(projectId)
}

/**
 * 启动时一次性清理 localStorage 中已被毒化的非法 projectId。
 * 必须在 main.ts 应用挂载前调用，避免坏值在整个会话内卡死所有依赖 RLS 的 API。
 */
export function sweepInvalidIdentityStorage(): void {
  let localStored: string | null = null
  let sessionStored: string | null = null
  try {
    localStored = localStorage.getItem(PROJECT_ID_STORAGE_KEY)
  } catch {
    localStored = null
  }
  try {
    sessionStored = sessionStorage.getItem(PROJECT_ID_STORAGE_KEY)
  } catch {
    sessionStored = null
  }
  if (localStored !== null && !isValidProjectId(localStored)) {
    if (typeof console !== 'undefined') {
      logger.warn(
        `[identity] 检测到非法 projectId='${localStored}' 已自动清理；将回退到默认/URL/token 链路`,
      )
    }
    removeLocalProjectId()
  }
  if (sessionStored !== null && !isValidProjectId(sessionStored)) {
    removeSessionProjectId()
  }
}

export function getUserId(userId?: string | null): string {
  const explicitUserId = normalizeIdentityValue(userId)
  if (explicitUserId) {
    persistLocalValue('userId', explicitUserId)
    return explicitUserId
  }

  const tokenUserId = readUserIdFromToken()
  if (tokenUserId) {
    persistLocalValue('userId', tokenUserId)
    return tokenUserId
  }

  const urlUserId = readQueryValue(USER_ID_QUERY_KEYS)
  if (urlUserId) {
    persistLocalValue('userId', urlUserId)
    return urlUserId
  }

  const storedUserId = readLocalValue('userId')
  if (storedUserId) {
    return storedUserId
  }

  const defaultUserId = normalizeIdentityValue(import.meta.env.VITE_DEFAULT_USER_ID) || LEGACY_DEFAULT_USER_ID
  persistLocalValue('userId', defaultUserId)
  return defaultUserId
}

export function resolveUserId(userId?: string | null): string {
  return getUserId(userId)
}

/**
 * 当前登录用户展示名（与 auth store 登录后写入的 localStorage `userName` 一致），
 * 供 API 请求头 X-Username 使用，便于后端审计字段写入真实姓名。
 */
export function getUserDisplayName(): string {
  try {
    const n = localStorage.getItem('userName')
    return normalizeUserDisplayName(n)
  } catch {
    return ''
  }
}
